import { apiFetch, clearCachedCsrfToken } from './client';

export const loginUser = async ({ username, password }) => {
  clearCachedCsrfToken();
  return apiFetch('/auth/login', {
    method: 'POST',
    body: { username, password },
    skipCsrf: true,
  });
};

export const registerUser = async ({ username, password, role, secretKey, email, fullName, company, department, plant, team, project, requestType, note }) => {
  return apiFetch('/auth/register', {
    method: 'POST',
    body: { username, password, role, secretKey, email, fullName, company, department, plant, team, project, requestType, note },
    skipCsrf: true,
  });
};

export const logoutUser = async () => {
  const result = await apiFetch('/auth/logout', {
    method: 'POST',
  });
  clearCachedCsrfToken();
  return result;
};

export const checkAuth = async () => {
  try {
    return await apiFetch('/check-auth');
  } catch (error) {
    if (error.status === 401 || error.status === 403) return { authenticated: false, message: error.message };
    throw error;
  }
};

export const fetchAdminUsers = async () => apiFetch('/auth/admin/users');
export const fetchAdminUserDetail = async (id) => apiFetch(`/auth/admin/users/${id}/detail`);
export const fetchApprovalEmailStatus = async () => apiFetch('/auth/admin/approval-email-status');
export const fetchLicensePackages = async () => apiFetch('/auth/admin/license-packages');
export const fetchLicenseAcceptanceReport = async ({ limit = 200, search = '', packageKey = '' } = {}) => {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (search) params.set('search', search);
  if (packageKey) params.set('packageKey', packageKey);
  return apiFetch(`/auth/admin/license-acceptances?${params.toString()}`);
};

export const createAdminUser = async ({ username, password, email, role, isActive, requireEmailApproval, requireLicenseAgreement, fullName, company, department, plant, team, project, requestType, adminNote, licensePackage }) => {
  return apiFetch('/auth/admin/users', {
    method: 'POST',
    body: { username, password, email, role, isActive, requireEmailApproval, requireLicenseAgreement, fullName, company, department, plant, team, project, requestType, adminNote, licensePackage },
  });
};

export const updateAdminUser = async (id, { username, password, email, role, isActive, requiresLicenseAcceptance, fullName, company, department, plant, team, project, requestType, adminNote, licensePackage, onboardingCompletedAt }) => {
  const payload = { username, email, role, isActive, requiresLicenseAcceptance, fullName, company, department, plant, team, project, requestType, adminNote, licensePackage, onboardingCompletedAt };
  if (password) payload.password = password;

  return apiFetch(`/auth/admin/users/${id}`, {
    method: 'PUT',
    body: payload,
  });
};

export const assignAdminUserToMe = async (id) =>
  apiFetch(`/auth/admin/users/${id}/assign-to-me`, {
    method: 'POST',
    body: {},
  });

export const unassignAdminUser = async (id) =>
  apiFetch(`/auth/admin/users/${id}/unassign`, {
    method: 'POST',
    body: {},
  });

export const setAdminUserAssignment = async (id, assignedAdminUserId) =>
  apiFetch(`/auth/admin/users/${id}/assignment`, {
    method: 'POST',
    body: { assignedAdminUserId },
  });

export const startUserLicenseTrial = async (id) =>
  apiFetch(`/auth/admin/users/${id}/license/trial`, {
    method: 'POST',
    body: {},
  });

export const activateUserPaidLicense = async (id, expiresAt) =>
  apiFetch(`/auth/admin/users/${id}/license/activate`, {
    method: 'POST',
    body: { expiresAt },
  });

export const deactivateUserLicense = async (id, reason = '') =>
  apiFetch(`/auth/admin/users/${id}/license/deactivate`, {
    method: 'POST',
    body: { reason },
  });

export const deleteAdminUser = async (id) => apiFetch(`/auth/admin/users/${id}`, { method: 'DELETE' });

export const sendAuthHeartbeat = async (clientMeta = {}) => {
  try {
    return await apiFetch('/auth/heartbeat', {
      method: 'POST',
      body: { clientMeta },
    });
  } catch (error) {
    if (error.status === 401 || error.status === 403) return { authenticated: false, message: error.message };
    throw error;
  }
};

export const fetchOnlineUsers = async () => apiFetch('/auth/admin/online-users');
export const fetchApprovalRequests = async (limit = 20) => apiFetch(`/auth/admin/approval-requests?limit=${limit}`);
export const fetchAccessRequests = async (limit = 20) => apiFetch(`/auth/admin/access-requests?limit=${limit}`);
export const resendApprovalRequest = async (id) =>
  apiFetch(`/auth/admin/approval-requests/${id}/resend`, {
    method: 'POST',
  });
export const cancelApprovalRequest = async (id, { reason = '' } = {}) =>
  apiFetch(`/auth/admin/approval-requests/${id}/cancel`, {
    method: 'POST',
    body: { reason },
  });
export const approveAccessRequest = async (id, { requireLicenseAgreement = false, internalNote = '', licensePackage = '' } = {}) =>
  apiFetch(`/auth/admin/access-requests/${id}/approve`, {
    method: 'POST',
    body: { requireLicenseAgreement, internalNote, licensePackage },
  });
export const declineAccessRequest = async (id, { declineReason = '', internalNote = '' } = {}) =>
  apiFetch(`/auth/admin/access-requests/${id}/decline`, {
    method: 'POST',
    body: { declineReason, internalNote },
  });

export const fetchActivitySessions = async (options = {}) => {
  const params = new URLSearchParams();
  const {
    limit = 200,
    username = '',
    role = '',
    search = '',
    startDate = '',
    endDate = '',
    status = '',
  } = options;

  params.set('limit', String(limit));
  if (username) params.set('username', username);
  if (role) params.set('role', role);
  if (search) params.set('search', search);
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (status) params.set('status', status);

  return apiFetch(`/auth/admin/activity-sessions?${params.toString()}`);
};

export const sendAuthPageVisit = async (path, clientMeta = {}) => {
  try {
    return await apiFetch('/auth/visit', {
      method: 'POST',
      body: { path, clientMeta },
    });
  } catch (error) {
    if (error.status === 401 || error.status === 403) return { authenticated: false, message: error.message };
    throw error;
  }
};

export const sendAuthDisconnect = async (reason = 'pagehide', clientMeta = {}) => {
  try {
    return await apiFetch('/auth/disconnect', {
      method: 'POST',
      body: { reason, clientMeta },
      keepalive: true,
    });
  } catch (error) {
    return { ok: false, message: error.message };
  }
};

export const blockIpAddress = async (ipAddress, note = '') => {
  return apiFetch('/auth/admin/block-ip', {
    method: 'POST',
    body: { ipAddress, note },
  });
};

export const unblockIpAddress = async (ipAddress) =>
  apiFetch('/auth/admin/unblock-ip', {
    method: 'POST',
    body: { ipAddress },
  });

export const fetchApprovalRequest = async (token) =>
  apiFetch(`/auth/approval/request?token=${encodeURIComponent(token)}`, {
    skipCsrf: true,
  });

export const confirmApprovalRequest = async (token, password) =>
  apiFetch('/auth/approval/confirm', {
    method: 'POST',
    body: { token, password },
  });

export const confirmApprovalRequestWithLicense = async (token, password, acceptedLicense, acceptedCommercialTerms) =>
  apiFetch('/auth/approval/confirm', {
    method: 'POST',
    body: { token, password, acceptedLicense, acceptedCommercialTerms },
  });

export const resendApprovalRequestFromToken = async (token) =>
  apiFetch('/auth/approval/resend', {
    method: 'POST',
    body: { token },
  });

export const completeOnboardingChecklist = async () =>
  apiFetch('/auth/onboarding/complete', {
    method: 'POST',
    body: {},
  });

export const fetchOwnProfile = async () => apiFetch('/auth/profile');

export const updateOwnProfile = async (payload) =>
  apiFetch('/auth/profile', {
    method: 'PUT',
    body: payload,
  });

export const changeOwnPassword = async ({ currentPassword, newPassword }) =>
  apiFetch('/auth/profile/password', {
    method: 'POST',
    body: { currentPassword, newPassword },
  });

export const requestAccountExtension = async ({ requestedPlan, note }) =>
  apiFetch('/auth/profile/extension-request', {
    method: 'POST',
    body: { requestedPlan, note },
  });

export const fetchMessagingInbox = async () => apiFetch('/auth/messages/bootstrap');

export const fetchUnreadMessageSummary = async () => apiFetch('/auth/messages/unread-summary');

export const fetchMessageConversation = async (contactId) =>
  apiFetch(`/auth/messages/conversations/${contactId}`);

export const fetchTeamMessageConversation = async (teamAdminUserId) =>
  apiFetch(`/auth/messages/team-conversations/${teamAdminUserId}`);

export const markMessageConversationRead = async (contactId) =>
  apiFetch(`/auth/messages/conversations/${contactId}/read`, {
    method: 'POST',
    body: {},
  });

export const markMessageConversationUnread = async (contactId) =>
  apiFetch(`/auth/messages/conversations/${contactId}/unread`, {
    method: 'POST',
    body: {},
  });

export const markTeamMessageConversationRead = async (teamAdminUserId) =>
  apiFetch(`/auth/messages/team-conversations/${teamAdminUserId}/read`, {
    method: 'POST',
    body: {},
  });

export const markTeamMessageConversationUnread = async (teamAdminUserId) =>
  apiFetch(`/auth/messages/team-conversations/${teamAdminUserId}/unread`, {
    method: 'POST',
    body: {},
  });

export const sendDirectAppMessage = async ({ recipientUserId, text, imageDataUrl = '', reportId = null, fileName = '', fileMimeType = '', fileDataUrl = '', fileIsCompressed = false, fileOriginalName = '', fileOriginalMimeType = '', priority = 'normal', messageKind = 'message', parentMessageId = null, mentionUserIds = [] }) =>
  apiFetch('/auth/messages', {
    method: 'POST',
    body: { recipientUserId, text, imageDataUrl, reportId, fileName, fileMimeType, fileDataUrl, fileIsCompressed, fileOriginalName, fileOriginalMimeType, priority, messageKind, parentMessageId, mentionUserIds },
  });

export const sendTeamAppMessage = async ({ teamAdminUserId, text, imageDataUrl = '', reportId = null, fileName = '', fileMimeType = '', fileDataUrl = '', fileIsCompressed = false, fileOriginalName = '', fileOriginalMimeType = '', priority = 'normal', messageKind = 'message', parentMessageId = null, mentionUserIds = [] }) =>
  apiFetch('/auth/messages/team', {
    method: 'POST',
    body: { teamAdminUserId, text, imageDataUrl, reportId, fileName, fileMimeType, fileDataUrl, fileIsCompressed, fileOriginalName, fileOriginalMimeType, priority, messageKind, parentMessageId, mentionUserIds },
  });
